import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FleetTabs, PageHeader, Pill, fmtDate, errText } from './common';

const ALERT_COLORS = { sent: 'var(--success)', pending: 'var(--warning)', failed: 'var(--danger)' };
const SEVERITY_COLORS = { high: 'var(--danger)', medium: 'var(--warning)', test: 'var(--text-muted)' };

function SiemPanel() {
  const [s, setS] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    axios.get('/cve-fleet/siem').then(r => setS(r.data)).catch(e => setError(errText(e, 'Failed to load SIEM status')));
  }, []);
  useEffect(() => { load(); }, [load]);

  async function test() {
    if (!window.confirm('Send one event labelled TEST to Elastic? Tell the SOC first so nobody acts on it.')) return;
    setBusy(true);
    try { await axios.post('/cve-fleet/siem/test'); setTimeout(load, 3000); }
    catch (e) { alert(errText(e, 'Failed to queue the test event')); }
    finally { setBusy(false); }
  }

  return (
    <div className="card" style={{ marginBottom: 16, fontSize: 13 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <h4 style={{ margin: 0 }}>Elastic Security alerts</h4>
        {s && (s.configured ? <Pill label="configured" color="var(--success)" /> : <Pill label="not configured" color="var(--danger)" />)}
        {s?.configured && <span style={{ color: 'var(--text-muted)' }}>{s.url} · index {s.index}{s.ca_pinned ? ' · CA pinned' : ''} · last sent {fmtDate(s.last_sent_at)}</span>}
        <div style={{ flex: 1 }} />
        <button className="btn btn-secondary" disabled={busy || !s?.configured} onClick={test}>Send test event</button>
      </div>
      <p style={{ margin: '8px 0', color: 'var(--text-muted)' }}>
        Raised when a detect report shows new attack signs: TCP-related kernel errors or kernel crash dumps (high), or
        inbound IPv4 TCP packets with IP options hitting the drop or monitor rule (medium). Repeats with nothing new are not sent.
      </p>
      {error && <div className="error-msg">{error}</div>}
      {s && s.alerts.length > 0 && (
        <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ textAlign: 'left', fontSize: 12, color: 'var(--text-muted)' }}>
            <th style={{ padding: 6 }}>Raised</th><th style={{ padding: 6 }}>Server</th><th style={{ padding: 6 }}>Severity</th>
            <th style={{ padding: 6 }}>Delivery</th><th style={{ padding: 6 }}>Summary</th></tr></thead>
          <tbody>{s.alerts.map(a => (
            <tr key={a.id} style={{ borderTop: '1px solid var(--border)' }}>
              <td style={{ padding: 6, whiteSpace: 'nowrap' }}>{fmtDate(a.created_at)}</td>
              <td style={{ padding: 6 }}>{a.severity === 'test' ? a.host_cn : <Link to={`/cve-fleet/hosts/${encodeURIComponent(a.host_cn)}`}>{a.host_cn}</Link>}</td>
              <td style={{ padding: 6 }}><Pill label={a.severity} color={SEVERITY_COLORS[a.severity]} /></td>
              <td style={{ padding: 6 }} title={a.last_error || ''}><Pill label={a.state} color={ALERT_COLORS[a.state]} />{a.attempts > 1 ? ` after ${a.attempts} tries` : ''}{a.last_error && a.state !== 'sent' ? <div style={{ fontSize: 11, color: 'var(--danger)' }}>{a.last_error.slice(0, 120)}</div> : null}</td>
              <td style={{ padding: 6 }}>{a.summary}</td>
            </tr>))}</tbody>
        </table>
      )}
    </div>
  );
}

export default function FleetAudit() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    axios.get('/cve-fleet/audit').then(r => setRows(r.data.audit)).catch(e => setError(errText(e, 'Failed to load the audit log')));
  }, []);
  return (
    <div>
      <PageHeader />
      <div className="page-body">
        <FleetTabs />
        <SiemPanel />
        {error && <div className="error-msg">{error}</div>}
        {!rows ? <div className="spinner" /> : (
          <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ textAlign: 'left', fontSize: 12, color: 'var(--text-muted)' }}>
              <th style={{ padding: 8 }}>When</th><th style={{ padding: 8 }}>Who</th><th style={{ padding: 8 }}>From</th>
              <th style={{ padding: 8 }}>Action</th><th style={{ padding: 8 }}>Detail</th></tr></thead>
            <tbody>{rows.map(r => (
              <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={{ padding: 8, whiteSpace: 'nowrap' }}>{fmtDate(r.at)}</td>
                <td style={{ padding: 8 }}>{r.actor}</td>
                <td style={{ padding: 8 }}>{r.actor_ip || '—'}</td>
                <td style={{ padding: 8 }}><code>{r.action}</code></td>
                <td style={{ padding: 8, fontSize: 12, wordBreak: 'break-word' }}>{r.detail ? JSON.stringify(r.detail) : ''}</td>
              </tr>))}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}
